import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ADMIN } from '@/constants/testIds';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(d => d.msg || JSON.stringify(d)).join(' '));
      else setError('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-0 mb-6">
            <span className="font-serif font-bold text-xl text-[#153E5C] tracking-tight">NKP</span>
            <span className="ml-2 font-mono text-[0.58rem] tracking-[0.32em] uppercase font-medium text-[#0E5A57]">VENTURES</span>
          </Link>
          <h1 className="font-serif text-2xl font-bold text-[#153E5C]">Admin Login</h1>
          <p className="text-sm text-[#6B7280] mt-1 font-sans">Sign in to manage your website</p>
        </div>

        <form data-testid={ADMIN.loginForm} onSubmit={handleSubmit} className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-sm">
          <div className="mb-5">
            <label className="block text-sm font-medium text-[#374151] mb-1.5 font-sans">Email</label>
            <input
              data-testid={ADMIN.loginEmail}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="admin@pkpcommerce.com"
              className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl text-sm font-sans focus:outline-none focus:ring-2 focus:ring-[#0E5A57]/20 focus:border-[#0E5A57] transition-colors duration-150"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#374151] mb-1.5 font-sans">Password</label>
            <div className="relative">
              <input
                data-testid={ADMIN.loginPassword}
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Your password"
                className="w-full px-4 py-3 pr-12 border border-[#E5E7EB] rounded-xl text-sm font-sans focus:outline-none focus:ring-2 focus:ring-[#0E5A57]/20 focus:border-[#0E5A57] transition-colors duration-150"
              />
              <button type="button" onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#374151] transition-colors duration-150">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 font-sans">
              {error}
            </div>
          )}

          <button
            data-testid={ADMIN.loginSubmit}
            type="submit"
            disabled={loading}
            className="w-full bg-[#0E5A57] hover:bg-[#2E7D7A] disabled:opacity-60 text-white font-medium py-3 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2 text-sm"
          >
            {loading ? <><Loader className="w-4 h-4 animate-spin" /> Signing in...</> : 'Sign In'}
          </button>
        </form>

        <p className="text-center mt-6 text-sm text-[#9CA3AF] font-sans">
          <Link to="/" className="text-[#0E5A57] hover:text-[#2E7D7A] transition-colors duration-150">← Back to website</Link>
        </p>
      </motion.div>
    </div>
  );
}
